/* External */
import {
  createToolboxTvService,
  shouldUpdateToken,
} from '@dtvgo/toolbox-service';
import { createAxiosWithAuth } from '@dtvgo/axios-interceptor-retry-auth';
import jwtDecode from 'jwt-decode';

/* Utils */
import {
  AUTH_CLIENT_ID,
  APP_VERSION,
  ENVIRONMENT,
  APP_THEME,
  TOOLBOX_OPEN_AM_KEY,
  TOOLBOX_HOST,
  MINUTES_BEFORE_TOKEN_REFRESH,
  regionAppSupport,
  notOsRegionApp,
} from '../utils/env';
import { REGIONS, STORAGE_COUNTRY } from '../utils/constants';
import { getConfig, getUserCountry } from '../utils/configUtils';
import platform from '../utils/platform';
import {
  getAccessTokenData, setAccessTokenData, onSessionError, ErrorCodes,
} from '../utils/accessTokenData';
import { getDeviceId } from '../utils/deviceIdUtils';

const deviceId = getDeviceId();

export const tokens = {
  get: async () => {
    const response = await getAccessTokenData();
    return {
      access_token: response?.accessToken,
      id_token: response?.idToken,
      refresh_token: response?.refreshToken,
    };
  },
  set: (response) => {
    setAccessTokenData({
      accessToken: response.access_token,
      idToken: response.id_token,
      refreshToken: response.refresh_token,
    });
  },
  fail: onSessionError,
  version: 'v2',
};

export const headers = () => ({
  clientId: AUTH_CLIENT_ID,
  appVersion: APP_VERSION,
  profileToken: JSON.parse(localStorage.getItem('profile'))?.profileToken,
  environment: ENVIRONMENT,
  appName: APP_THEME,
});

export const getRegion = async () => {
  const dataConfig = await getConfig() || {};

  const dataGetCountry = await getUserCountry() || {};
  const userCountry = dataGetCountry?.userCountry;

  const hasPlatformNotSupportRegion = notOsRegionApp.has(platform.platformName);

  if (regionAppSupport && !hasPlatformNotSupportRegion) {
    return regionAppSupport === REGIONS.br ? REGIONS.br : REGIONS.ssla;
  }

  if (!!userCountry && userCountry !== dataConfig?.userCountry) {
    localStorage.setItem(STORAGE_COUNTRY, userCountry);
    return userCountry === REGIONS.br ? REGIONS.br : REGIONS.ssla;
  }

  return dataConfig?.userCountry === REGIONS.br ? REGIONS.br : REGIONS.ssla;
};

export const tvService = createToolboxTvService({
  host: TOOLBOX_HOST,
  deviceId,
  clientHeadersOptions: headers(),
  region: getRegion,
  tokens,
  authorization: TOOLBOX_OPEN_AM_KEY,
});

export const authInterceptor = createAxiosWithAuth(
  tvService.axiosInstance,
  tokens,
  getRegion,
);

let refreshIdTokenPromise;

export const refreshIdToken = async () => {
  if (refreshIdTokenPromise) {
    return refreshIdTokenPromise;
  }

  refreshIdTokenPromise = (async () => {
    try {
      const { refreshToken, idToken } = await getAccessTokenData() || {};
      const { businessUnit, msoProvider } = jwtDecode(idToken);
      await tvService.v2.tv.refreshToken(refreshToken, { businessUnit, msoProvider });
    } finally {
      refreshIdTokenPromise = undefined;
    }
  })();
  return refreshIdTokenPromise;
};

export const checkToken = async () => {
  const offsetMinutes = MINUTES_BEFORE_TOKEN_REFRESH;
  const { refreshToken, idToken } = await getAccessTokenData() || {};
  const {
    isIdTokenExpired, isIdTokenExpiring, isRefreshTokenExpired,
  } = shouldUpdateToken({ refreshToken, idToken, offsetMinutes });

  if (isRefreshTokenExpired) {
    onSessionError({
      axiosError: { response: { data: { code: ErrorCodes.TOO_MUCH_TIME_AWAY } } },
      undefined,
    });
    return false;
  }

  if (!isIdTokenExpired && isIdTokenExpiring) {
    await refreshIdToken();
    return true;
  }
  return isIdTokenExpired;
};
